
import React, { useState, useEffect } from 'react';
import { X, MessageCircle, Check, Gift, Edit3, Save, Loader2, RotateCcw, Phone, AlertCircle } from 'lucide-react';
import { Contact, User } from '../types';
import { supabase } from '../supabase';

interface BirthdayModalProps {
  contact: Contact;
  currentUser: User;
  onClose: () => void;
}

const BirthdayModal: React.FC<BirthdayModalProps> = ({ contact, currentUser, onClose }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(0);
  const [editedMessage, setEditedMessage] = useState('');
  const [isManagingTemplates, setIsManagingTemplates] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Templates Padrão (Fallback)
  const defaultTemplates = [
    `Oi, {primeiro-nome}, tudo bem? 🎉\nPassando por aqui para te desejar um feliz aniversário, com muita saúde, felicidades e sucesso 🎂✨\nConte sempre comigo. Um abraço 🤝\n{meu-nome}`,
    
    `Fala {primeiro-nome}! 🎈\nHoje é o seu dia! Que este novo ciclo traga muitas conquistas e alegrias. Aproveite muito!\nParabéns! 👏👏\n{meu-nome}`,
    
    `Olá, {nome-completo}. 🎉\nQuero deixar aqui meus votos de um excelente aniversário. Que você tenha um ano iluminado e cheio de realizações.\nFelicidades! 🎁\n{meu-nome}`
  ];

  // Estado dos templates (carrega do usuário ou usa o padrão)
  const [templates, setTemplates] = useState<string[]>(
    currentUser.settings?.birthday_templates && currentUser.settings.birthday_templates.length === 3 
      ? currentUser.settings.birthday_templates 
      : defaultTemplates
  );

  const firstName = contact.name.split(' ')[0];
  const hasPhone = contact.phones && contact.phones.length > 0 && contact.phones[0].trim() !== '';

  // Função que substitui as variáveis pelo valor real para envio
  const parseTemplate = (template: string) => {
    return template
      .replace(/{primeiro-nome}/gi, firstName)
      .replace(/\[Primeiro nome\]/gi, firstName) // Compatibilidade legado
      .replace(/{nome-completo}/gi, contact.name)
      .replace(/{meu-nome}/gi, currentUser.name)
      .replace(/\[INSIRA SEU NOME\]/gi, currentUser.name); // Compatibilidade legado
  };

  useEffect(() => {
    // Atualiza a mensagem editada quando muda o template selecionado
    setEditedMessage(parseTemplate(templates[selectedTemplate]));
  }, [selectedTemplate, templates, contact, currentUser]);

  const handleSendWhatsapp = () => {
    if (!hasPhone) return;
    
    const phone = contact.phones[0].replace(/\D/g, ''); // Remove tudo que não é número
    const encodedMessage = encodeURIComponent(editedMessage);
    const url = `https://wa.me/55${phone}?text=${encodedMessage}`;
    
    window.open(url, '_blank');
    onClose();
  };

  const handleSaveTemplates = async () => {
    setSaving(true);
    try {
      const newSettings = {
        ...currentUser.settings,
        birthday_templates: templates
      };

      const { error } = await supabase
        .from('profiles')
        .update({ settings: newSettings })
        .eq('id', currentUser.id);

      if (error) throw error;
      
      // Atualiza localmente o currentUser (hack rápido para refletir sem reload, idealmente atualizaria via prop)
      currentUser.settings = newSettings;
      
      setIsManagingTemplates(false);
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar modelos.");
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    if(confirm("Deseja restaurar os modelos originais?")) {
      setTemplates(defaultTemplates);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-navy-950/80 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div className="bg-white dark:bg-navy-900 w-full max-w-lg rounded-[32px] shadow-2xl flex flex-col overflow-hidden border border-neutral-200 dark:border-navy-800 relative max-h-[90vh]" onClick={e => e.stopPropagation()}>
        
        {/* Header Festivo */}
        <div className="bg-gradient-to-r from-pink-500 to-rose-500 p-8 text-center relative overflow-hidden shrink-0">
           <div className="absolute top-0 left-0 w-full h-full opacity-20 bg-[url('https://www.transparenttextures.com/patterns/confetti.png')]"></div>
           <button onClick={onClose} className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/40 rounded-full text-white transition-all"><X size={20}/></button>
           <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl text-3xl font-black text-rose-500 relative z-10">
              <Gift size={40} />
           </div>
           <h2 className="text-2xl font-black text-white tracking-tighter relative z-10">Feliz Aniversário!</h2>
           <p className="text-white/90 font-medium text-sm mt-1 relative z-10">
             {isManagingTemplates ? 'Editar Modelos de Mensagem' : <>Enviar mensagem para <span className="font-bold">{contact.name}</span></>}
           </p>
        </div>

        {/* Corpo com Scroll */}
        <div className="p-8 space-y-6 overflow-y-auto custom-scrollbar">
           
           {isManagingTemplates ? (
             // MODO DE EDIÇÃO DE TEMPLATES
             <div className="space-y-4 animate-in slide-in-from-right-4">
                <div className="flex justify-between items-center mb-2">
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Edite seus 3 modelos padrão</p>
                   <button onClick={handleResetDefaults} className="text-[9px] font-bold text-rose-500 hover:underline flex items-center gap-1"><RotateCcw size={10}/> Restaurar Padrão</button>
                </div>
                {templates.map((tmpl, idx) => (
                  <div key={idx} className="space-y-1">
                    <label className="text-[9px] font-bold text-slate-400 uppercase">Modelo {idx + 1}</label>
                    <textarea 
                      value={tmpl}
                      onChange={(e) => {
                        const newTemplates = [...templates];
                        newTemplates[idx] = e.target.value;
                        setTemplates(newTemplates);
                      }}
                      className="w-full h-24 bg-slate-50 dark:bg-navy-950 border border-slate-200 dark:border-navy-700 rounded-xl p-3 text-xs font-medium text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-rose-500 resize-none"
                      placeholder={`Escreva o modelo ${idx + 1}...`}
                    />
                  </div>
                ))}
                <div className="flex gap-2 pt-2">
                   <button onClick={() => setIsManagingTemplates(false)} className="flex-1 py-3 text-slate-400 font-bold text-xs uppercase hover:bg-slate-50 dark:hover:bg-navy-800 rounded-xl transition-all">Cancelar</button>
                   <button onClick={handleSaveTemplates} disabled={saving} className="flex-1 bg-navy-950 dark:bg-white text-white dark:text-navy-950 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2">
                      {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar Modelos
                   </button>
                </div>
                <div className="bg-slate-50 dark:bg-navy-950 p-4 rounded-xl border border-slate-100 dark:border-navy-800">
                  <p className="text-[9px] font-bold text-slate-500 uppercase mb-2">Variáveis Disponíveis:</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2 py-1 bg-white dark:bg-navy-900 border rounded text-[10px] font-mono text-slate-600 dark:text-slate-300">{`{primeiro-nome}`}</span>
                    <span className="px-2 py-1 bg-white dark:bg-navy-900 border rounded text-[10px] font-mono text-slate-600 dark:text-slate-300">{`{nome-completo}`}</span>
                    <span className="px-2 py-1 bg-white dark:bg-navy-900 border rounded text-[10px] font-mono text-slate-600 dark:text-slate-300">{`{meu-nome}`}</span>
                  </div>
                </div>
             </div>
           ) : (
             // MODO DE SELEÇÃO E ENVIO
             <div className="animate-in slide-in-from-left-4 space-y-6">
                
                {/* Exibição do Telefone */}
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-neutral-50 dark:bg-navy-950 border border-neutral-100 dark:border-navy-800">
                   <div className={`p-2 rounded-xl ${hasPhone ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-500'}`}>
                      <Phone size={18} />
                   </div>
                   <div>
                      <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Whatsapp de Destino</p>
                      <p className="text-sm font-bold text-navy-900 dark:text-white">
                        {hasPhone ? contact.phones[0] : 'Nenhum número cadastrado'}
                      </p>
                   </div>
                </div>

                <div className="space-y-3">
                    <div className="flex justify-between items-center">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Escolha um modelo</label>
                        <button onClick={() => setIsManagingTemplates(true)} className="text-[9px] font-black text-rose-500 uppercase tracking-widest hover:underline flex items-center gap-1"><Edit3 size={10}/> Gerenciar Modelos</button>
                    </div>
                    <div className="space-y-2">
                      {templates.map((template, index) => (
                          <button 
                            key={index}
                            onClick={() => setSelectedTemplate(index)}
                            className={`w-full text-left p-4 rounded-2xl border-2 transition-all relative ${selectedTemplate === index ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/10' : 'border-slate-100 dark:border-navy-800 hover:border-rose-200'}`}
                          >
                            <p className="text-xs font-medium text-slate-600 dark:text-slate-300 line-clamp-2 whitespace-pre-line leading-relaxed">{parseTemplate(template)}</p>
                            {selectedTemplate === index && <div className="absolute top-3 right-3 text-rose-500"><CheckCircle2 size={16} /></div>}
                          </button>
                      ))}
                    </div>
                </div>

                <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2 flex items-center gap-2"><Edit3 size={12}/> Revisar Mensagem Final</label>
                    <textarea 
                      value={editedMessage} 
                      onChange={(e) => setEditedMessage(e.target.value)} 
                      className="w-full h-32 bg-slate-50 dark:bg-navy-950 border border-slate-200 dark:border-navy-700 rounded-2xl p-4 text-sm font-medium text-slate-700 dark:text-slate-200 outline-none focus:ring-2 focus:ring-rose-500 resize-none"
                    />
                </div>

                <button 
                  onClick={handleSendWhatsapp}
                  disabled={!hasPhone}
                  className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl flex items-center justify-center gap-3 transition-all transform hover:scale-[1.02] active:scale-95 ${hasPhone ? 'bg-[#25D366] hover:bg-[#128C7E] text-white shadow-green-500/20' : 'bg-slate-200 dark:bg-navy-800 text-slate-400 cursor-not-allowed'}`}
                >
                    {hasPhone ? <><MessageCircle size={20} /> Enviar no WhatsApp</> : <><AlertCircle size={20} /> Sem Telefone</>}
                </button>
             </div>
           )}

        </div>
      </div>
    </div>
  );
};

// Helper icon
const CheckCircle2 = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
);

export default BirthdayModal;
